import React, { memo, PropsWithChildren } from 'react';

import { useRouter } from 'next/router';

import { ErrorPage } from '@explorer/components/shared';
import { CoalitionLoading } from '@explorer/core/presets';
import { Permissions, useAuth, useUser } from '@explorer/stores';

import { PageHeadWrapper, PageHeadWrapperProps } from '../PageHeadWrapper';

/**
 * AuthenticationWrapper
 * @description wrapped around main app, handles authentication rendering and calls hook with login
 */
export const AuthenticationWrapper: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const router = useRouter();
  const { isLoading } = useAuth();
  const { userAuthenticated } = useUser();

  if (router.pathname === '/login/callback') {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  if (userAuthenticated && !isLoading) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
  }

  return <CoalitionLoading />;
};

/**
 * PageAuthenticationWrapper
 * @description handles user permissions on every view wrapped with component.
 */
export const PageAuthenticationWrapper = memo(
  //
  function PageAuthenticationWrapper({
    pageMeta,
    permissions = [],
    userHasAtLeastOnePermission = false,
    children = null,
  }: PageAuthenticationWrapperProps) {
    const { userHasPermissions, userHasSomePermissions, user } = useUser();
    const noPermissions = user.permissions.length === 0;

    const hasPermissions = userHasAtLeastOnePermission
      ? userHasSomePermissions(permissions)
      : userHasPermissions(permissions);

    if (noPermissions) {
      return <ErrorPage error="user-has-no-permissions" />;
    }

    return (
      <PageHeadWrapper pageMeta={pageMeta}>
        {hasPermissions ? (
          // eslint-disable-next-line react/jsx-no-useless-fragment
          <>{children}</>
        ) : (
          <ErrorPage error="user-not-allowed" />
        )}
      </PageHeadWrapper>
    );
  },
);

PageAuthenticationWrapper.displayName = 'PageAuthenticationWrapper';

/**
 * PageAuthenticationWrapperProps
 */
export interface PageAuthenticationWrapperProps extends PageHeadWrapperProps {
  readonly permissions?: Permissions[];
  readonly userHasAtLeastOnePermission?: boolean;
}
